import { useDispatch, useSelector } from 'react-redux';
import discountDisplacementSlice, {
  CouponDisplacedPoints,
  DisplacedByOffer,
  PointsDisplacedCoupon,
  selectCouponDisplacedPoints,
  selectDisplacedByOffer,
  selectPointsDisplacedCoupon,
} from '../../store/slices/DiscountDisplacementSlice';
import giftCardSlice from '../../store/slices/GiftCardSlice';
import usePromoCode from '../../hooks/order/usePromoCode';
import useGiftCard from '../gift-cards/hooks/useGiftCard';
import ReduxActions from '../../store/ReduxActions';
import { actionCreators } from '../../store/actions';
import { COUPON_TYPES } from '../../services/exports/Constants';

/**
 * FOO-2555 / PROD-407 — reverse-direction displacement (offer displaces a promo code or gift card).
 *
 * `record` is called at the single gesture where an offer wins ("Use offer instead"): it stores
 * what the offer displaced so the notice can name it ("Offer … applied. This removed CHEVERE.").
 * `revert` restores that discount — re-applying it naturally re-suppresses the offer, which is the
 * customer "switching back". `clear` drops the record once it is no longer relevant. (An offer and
 * a points redemption coexist, so points are never displaced by an offer.)
 */
export default function useDiscountDisplacement() {
  const dispatch = useDispatch();
  const displaced = useSelector(selectDisplacedByOffer);
  const displacedPoints = useSelector(selectCouponDisplacedPoints);
  const pointsDisplacedCoupon = useSelector(selectPointsDisplacedCoupon);
  const { applyPromoCode, removePromoCode } = usePromoCode();
  const { remove: removeGiftCard } = useGiftCard();

  const record = (payload: DisplacedByOffer) =>
    dispatch(discountDisplacementSlice.actions.setDisplacedByOffer(payload));

  const clear = () => dispatch(discountDisplacementSlice.actions.clear(undefined));

  const revert = () => {
    if (!displaced) {
      return;
    }

    clear();

    if (displaced.kind === 'promo' && displaced.promoCode) {
      applyPromoCode(displaced.promoCode, true);

      return;
    }

    if (displaced.kind === 'gift' && displaced.giftCard) {
      ReduxActions.dispatch(giftCardSlice.actions.apply(displaced.giftCard));
    }
  };

  // --- Promo/gift displaced a points redemption (forward-only; the record names the removed points). ---
  const recordPointsDisplacement = (payload: CouponDisplacedPoints) =>
    dispatch(discountDisplacementSlice.actions.setCouponDisplacedPoints(payload));

  const clearPointsDisplacement = () =>
    dispatch(discountDisplacementSlice.actions.clearCouponDisplacedPoints(undefined));

  const revertPoints = () => {
    if (!displacedPoints) {
      return;
    }

    clearPointsDisplacement();

    // Remove the coupon/gift that displaced the points, then restore the points redemption.
    displacedPoints.kind === 'gift' ? removeGiftCard() : removePromoCode();

    ReduxActions.dispatch(
      actionCreators.order.updateOrder({
        coupon_type: COUPON_TYPES.CASHBACK,
        coupon_details: null,
        cashback_points: displacedPoints.points,
      }),
    );
  };

  // --- A points redemption displaced a promo code / gift card (UX feedback item 3). ---
  const recordPointsDisplacedCoupon = (payload: PointsDisplacedCoupon) =>
    dispatch(discountDisplacementSlice.actions.setPointsDisplacedCoupon(payload));

  const clearPointsDisplacedCoupon = () =>
    dispatch(discountDisplacementSlice.actions.clearPointsDisplacedCoupon(undefined));

  const revertPointsDisplacedCoupon = () => {
    if (!pointsDisplacedCoupon) {
      return;
    }

    clearPointsDisplacedCoupon();

    // Remove the points, then restore the promo/gift (which re-clears the points — "switching back").
    ReduxActions.dispatch(
      actionCreators.order.updateOrder({ coupon_type: null, coupon_details: null, cashback_points: null }),
    );

    if (pointsDisplacedCoupon.kind === 'gift' && pointsDisplacedCoupon.giftCard) {
      ReduxActions.dispatch(giftCardSlice.actions.apply(pointsDisplacedCoupon.giftCard));

      return;
    }

    if (pointsDisplacedCoupon.kind === 'promo' && pointsDisplacedCoupon.label) {
      applyPromoCode(pointsDisplacedCoupon.label, true);
    }
  };

  return {
    displaced,
    record,
    clear,
    revert,
    displacedPoints,
    recordPointsDisplacement,
    clearPointsDisplacement,
    revertPoints,
    pointsDisplacedCoupon,
    recordPointsDisplacedCoupon,
    clearPointsDisplacedCoupon,
    revertPointsDisplacedCoupon,
  };
}
